import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Popover, Skeleton, Tooltip } from 'antd';
import { Icon } from 'shared/components/Icon';
import { useApiLoader } from 'shared/hooks';
import { getSchedulerOrgCalendarEventTypes } from 'shared/api';
import {
  ChatMessageBlockType,
  ChatMessageScheduleBlock,
  SchedulerCalendarEventType,
} from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { sendChatMessage } from 'shared/store/chat-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { SelfUser } from 'types/user';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import './style.less';

interface SendMeetingBookerButtonProps {
  className?: string;
  conversationId: number;
  visitorId: string;
}

const NAME = 'SendMeetingBookerButton';
export const SendMeetingBookerButton: React.FC<SendMeetingBookerButtonProps> = props => {
  const [open, setOpen] = useState(false);
  return (
    <div className={clsx([props.className, NAME, open && `${NAME}--active`])}>
      <Tooltip title="Send meeting calendar" placement="bottom">
        <Popover
          placement="top"
          content={<PopoverContent {...props} setOpen={setOpen} />}
          trigger="click"
          visible={open}
          destroyTooltipOnHide
          overlayClassName={`${NAME}__overlay`}
        >
          <button onClick={() => setOpen(!open)}>
            <Icon icon="calendarScheduleCheckmark" />
          </button>
        </Popover>
      </Tooltip>
    </div>
  );
};

const PopoverContent: React.FC<
  SendMeetingBookerButtonProps & {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }
> = ({ visitorId, conversationId, setOpen }) => {
  const dispatch = useAppDispatch();
  const eventTypes = useApiLoader(getSchedulerOrgCalendarEventTypes);
  const selfUser = useAppSelector(selectActiveUser);
  const users = useAppSelector(selectActiveOrgMembers);

  const handleSendMeetingBooker = async (
    calendarEventTypeId: ChatMessageScheduleBlock['calendarEventTypeId'],
  ) => {
    if (!selfUser) {
      return;
    }
    const message = makeMessage(visitorId, conversationId, calendarEventTypeId, selfUser);
    await dispatch(sendChatMessage(message));
    setOpen(false);
  };

  const eventTypesByAgent = useMemo(
    () =>
      (eventTypes.data || []).reduce((acc, et) => {
        if (et.userId === selfUser?.id) {
          (x => (acc[x] = [...(acc[x] || []), et]))('Your Meetings');
        } else {
          const otherUser = users.find(u => u.id === et.userId);
          const label = otherUser?.displayName || 'Unknown';
          (x => (acc[x] = [...(acc[x] || []), et]))(label);
        }
        return acc;
      }, {} as Record<string, SchedulerCalendarEventType[]>),
    [eventTypes.data, selfUser?.id, users],
  );

  const cn = (x: string) => `${NAME}__popover${x}`;
  return (
    <div className={cn('')}>
      <button className={cn('-close')} onClick={() => setOpen(false)}>
        <Icon icon="close" />
      </button>
      <Skeleton loading={!eventTypes.hasLoaded} active={true} />
      {Object.entries(eventTypesByAgent).map(([name, events]) => (
        <React.Fragment key={name}>
          <div className={cn('-event-cat')}>{name}</div>
          <div className={cn('-event-list')}>
            {events.map(et => {
              return (
                <div
                  className={cn('-event-list-item')}
                  key={et.id}
                  onClick={() => handleSendMeetingBooker(et.id)}
                >
                  <div className={cn('-event-list-item-left')}>
                    <div>{et.name}</div>
                    <span>{et.durationMinutes} min</span>
                  </div>
                  <Icon icon="send" />
                </div>
              );
            })}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const makeMessage = (
  visitorId: string,
  conversationId: number,
  calendarEventTypeId: ChatMessageScheduleBlock['calendarEventTypeId'],
  selfUser: SelfUser,
) => {
  return {
    visitorId,
    conversationId,
    content: {
      blocks: [
        {
          type: ChatMessageBlockType.SCHEDULE,
          calendarEventTypeId,
          calendarEventId: null,
          calendarEventEmailAddress: null,
          calendarEventStartTime: null,
          agent: {
            id: selfUser.id,
            name: selfUser.displayName,
            title: selfUser.title,
          },
        } as ChatMessageScheduleBlock,
      ],
    },
  };
};
